import { ReloadOutlined, VideoCameraOutlined } from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import useApi from "../../utils/useApi";
import CameraHistoryModal from "./CameraHistoryModal";

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

const REFETCH_INTERVAL_MS = 60_000;
const DEMO_CLIENT = "Ouran Demo";

function isLiveCamera(c) {
  return (
    c.current_status?.is_live &&
    c.is_expected_live !== false &&
    c.current_status?.error_code !== "rtmp_h265"
  );
}

function cameraSummary(cameras) {
  const liveCnt = cameras.filter(isLiveCamera).length;
  const totalCnt = cameras.length;
  const offlineCnt = cameras.filter(
    (c) =>
      c.is_expected_live !== false &&
      (c.current_status?.is_live === false ||
        c.current_status?.error_code === "rtmp_h265")
  ).length;
  const badgeStatus =
    liveCnt > 0 && offlineCnt === 0
      ? "success"
      : liveCnt > 0 && offlineCnt > 0
        ? "warning"
        : offlineCnt > 0
          ? "error"
          : "default";
  return { liveCnt, totalCnt, offlineCnt, badgeStatus };
}

function StatusTag({ camera }) {
  const { is_live, checked_at, error_code } = camera.current_status;
  const { is_expected_live, is_active } = camera;

  if (is_active === false) {
    return <Tag color="default">Inativa</Tag>;
  }
  if (is_live === null || is_live === undefined) {
    return <Tag color="default">Sem dados</Tag>;
  }
  if (is_expected_live === false) {
    return <Tag color="default">Fora do horário</Tag>;
  }
  if (is_live && error_code === "rtmp_h265") {
    return (
      <Tooltip title="Stream conectado mas codec H.265 não é suportado via RTMP — imagens indisponíveis">
        <Tag color="warning">Erro</Tag>
      </Tooltip>
    );
  }
  if (is_live) {
    return <Tag color="success">Ao vivo</Tag>;
  }
  return (
    <Tag color="error">
      Offline
      {checked_at && (
        <span style={{ marginLeft: 4, fontWeight: 400 }}>
          · {dayjs(checked_at).fromNow()}
        </span>
      )}
    </Tag>
  );
}

function SlaCell({ value }) {
  if (value === null || value === undefined)
    return <Text type="secondary">—</Text>;
  const color = value >= 99 ? "#52c41a" : value >= 95 ? "#faad14" : "#ff4d4f";
  return <span style={{ color, fontWeight: 600 }}>{value.toFixed(1)}%</span>;
}

function PanelSubtitle({ summary }) {
  return (
    <Space size={4} style={{ marginLeft: 4 }}>
      <Text type="secondary" style={{ fontWeight: 400 }}>
        {summary.liveCnt}/{summary.totalCnt} ao vivo
      </Text>
      {summary.offlineCnt > 0 && (
        <Text style={{ color: "#ff4d4f", fontWeight: 400 }}>
          · {summary.offlineCnt} offline
        </Text>
      )}
    </Space>
  );
}

export default function CamerasMonitor() {
  const { getCamerasMonitoring } = useApi();
  const [countdown, setCountdown] = useState(REFETCH_INTERVAL_MS / 1000);
  const [historyCamera, setHistoryCamera] = useState(null);

  const { data, isFetching, refetch, dataUpdatedAt } = useQuery({
    queryKey: ["cameras-monitoring"],
    queryFn: () => getCamerasMonitoring(DEMO_CLIENT).then((r) => r.data),
    refetchInterval: REFETCH_INTERVAL_MS,
    staleTime: 0,
  });

  useEffect(() => {
    setCountdown(REFETCH_INTERVAL_MS / 1000);
    const timer = setInterval(
      () => setCountdown((c) => (c > 0 ? c - 1 : 0)),
      1000
    );
    return () => clearInterval(timer);
  }, [dataUpdatedAt]);

  const cameras = data?.cameras ?? [];

  const total = cameras.length;
  const inactive = cameras.filter((c) => c.is_active === false).length;
  const outOfSchedule = cameras.filter(
    (c) => c.is_expected_live === false && c.is_active !== false
  ).length;
  const live = cameras.filter(isLiveCamera).length;
  const offline = cameras.filter(
    (c) =>
      c.is_expected_live !== false &&
      (c.current_status?.is_live === false ||
        c.current_status?.error_code === "rtmp_h265")
  ).length;

  const columns = [
    {
      title: "Câmera",
      dataIndex: "name",
      key: "name",
      width: 160,
      render: (name) => (
        <Space>
          <VideoCameraOutlined style={{ color: "#8c8c8c" }} />
          <Text strong>{name}</Text>
        </Space>
      ),
    },
    {
      title: "Tipo",
      dataIndex: "stream_type",
      key: "stream_type",
      width: 70,
      render: (t) => (
        <Tag style={{ fontFamily: "monospace" }}>
          {(t || "—").toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: "RTMP", value: "rtmp" },
        { text: "RTSP", value: "rtsp" },
      ],
      onFilter: (val, rec) => rec.stream_type === val,
    },
    {
      title: "Status",
      key: "status",
      width: 170,
      render: (_, cam) => <StatusTag camera={cam} />,
      filters: [
        { text: "Ao vivo", value: "live" },
        { text: "Erro", value: "h265" },
        { text: "Offline", value: "offline" },
        { text: "Fora do horário", value: "out_of_schedule" },
        { text: "Inativa", value: "inactive" },
      ],
      onFilter: (val, cam) => {
        if (val === "inactive") return cam.is_active === false;
        if (val === "out_of_schedule")
          return cam.is_expected_live === false && cam.is_active !== false;
        if (val === "h265")
          return (
            cam.current_status?.is_live === true &&
            cam.current_status?.error_code === "rtmp_h265"
          );
        if (val === "live") return isLiveCamera(cam);
        if (val === "offline")
          return (
            cam.current_status?.is_live === false &&
            cam.is_expected_live !== false
          );
        return true;
      },
    },
    {
      title: "Última verificação",
      key: "checked_at",
      width: 160,
      render: (_, cam) => {
        const t = cam.current_status?.checked_at;
        return t ? (
          <Tooltip title={dayjs(t).format("DD/MM/YYYY HH:mm:ss")}>
            <Text type="secondary">{dayjs(t).fromNow()}</Text>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
      sorter: (a, b) =>
        new Date(a.current_status?.checked_at || 0) -
        new Date(b.current_status?.checked_at || 0),
    },
    {
      title: "SLA 24h",
      key: "sla_24h",
      width: 90,
      render: (_, cam) => <SlaCell value={cam.sla?.last_24h} />,
      sorter: (a, b) => (a.sla?.last_24h ?? -1) - (b.sla?.last_24h ?? -1),
    },
    {
      title: "SLA 7d",
      key: "sla_7d",
      width: 90,
      render: (_, cam) => <SlaCell value={cam.sla?.last_7d} />,
      sorter: (a, b) => (a.sla?.last_7d ?? -1) - (b.sla?.last_7d ?? -1),
    },
    {
      title: "SLA 30d",
      key: "sla_30d",
      width: 90,
      render: (_, cam) => <SlaCell value={cam.sla?.last_30d} />,
      sorter: (a, b) => (a.sla?.last_30d ?? -1) - (b.sla?.last_30d ?? -1),
    },
    {
      title: "",
      key: "actions",
      width: 80,
      render: (_, cam) => (
        <Button size="small" type="link" onClick={() => setHistoryCamera(cam)}>
          Histórico
        </Button>
      ),
    },
  ];

  const groupedData = _groupByBranch(cameras);

  return (
    <div style={{ padding: "24px 32px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}>
        <Title level={4} style={{ margin: 0 }}>
          Monitoramento de Câmeras
        </Title>
        <Space>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Atualiza em {countdown}s
          </Text>
          <Button
            icon={<ReloadOutlined spin={isFetching} />}
            onClick={() => refetch()}
            size="small">
            Atualizar
          </Button>
        </Space>
      </div>

      {/* Summary cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col>
          <Card size="small" style={{ minWidth: 120, textAlign: "center" }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#52c41a" }}>
              {live}
            </div>
            <Text type="secondary">Ao vivo</Text>
          </Card>
        </Col>
        <Col>
          <Card size="small" style={{ minWidth: 120, textAlign: "center" }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#ff4d4f" }}>
              {offline}
            </div>
            <Text type="secondary">Offline</Text>
          </Card>
        </Col>
        <Col>
          <Card size="small" style={{ minWidth: 120, textAlign: "center" }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#8c8c8c" }}>
              {outOfSchedule}
            </div>
            <Text type="secondary">Fora do horário</Text>
          </Card>
        </Col>
        <Col>
          <Card size="small" style={{ minWidth: 120, textAlign: "center" }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#8c8c8c" }}>
              {inactive}
            </div>
            <Text type="secondary">Inativas</Text>
          </Card>
        </Col>
        <Col>
          <Card size="small" style={{ minWidth: 120, textAlign: "center" }}>
            <div style={{ fontSize: 28, fontWeight: 700 }}>{total}</div>
            <Text type="secondary">Total</Text>
          </Card>
        </Col>
      </Row>

      {/* Per-client collapsible sections */}
      <Collapse defaultActiveKey={[]} style={{ marginBottom: 24 }}>
        {groupedData.map(({ clientName, branches }) => {
          const allCams = branches.flatMap((b) => b.cameras);
          const cs = cameraSummary(allCams);
          return (
            <Collapse.Panel
              key={clientName}
              header={
                <Space>
                  <Badge status={cs.badgeStatus} />
                  <Text
                    strong
                    style={{
                      fontSize: 13,
                      color: "#595959",
                      textTransform: "uppercase",
                    }}>
                    {clientName}
                  </Text>
                  <PanelSubtitle summary={cs} />
                </Space>
              }>
              {/* Per-branch collapsible */}
              <Collapse defaultActiveKey={[]}>
                {branches.map(({ branchName, cameras: branchCameras }) => {
                  const bs = cameraSummary(branchCameras);
                  return (
                    <Collapse.Panel
                      key={branchName}
                      header={
                        <Space>
                          <Badge status={bs.badgeStatus} />
                          <Text strong>{branchName}</Text>
                          <PanelSubtitle summary={bs} />
                        </Space>
                      }>
                      <Table
                        dataSource={branchCameras}
                        columns={columns}
                        rowKey="id"
                        size="small"
                        pagination={false}
                        rowClassName={(cam) =>
                          cam.is_active !== false &&
                          cam.current_status?.is_live === false &&
                          cam.is_expected_live !== false
                            ? "cam-row-offline"
                            : ""
                        }
                      />
                    </Collapse.Panel>
                  );
                })}
              </Collapse>
            </Collapse.Panel>
          );
        })}
      </Collapse>

      {historyCamera && (
        <CameraHistoryModal
          camera={historyCamera}
          onClose={() => setHistoryCamera(null)}
        />
      )}
    </div>
  );
}

function _groupByBranch(cameras) {
  const clientMap = {};
  cameras.forEach((cam) => {
    const clientName = cam.client_name || "Sem cliente";
    const branchName = cam.branch_name || "Sem filial";
    if (!clientMap[clientName]) clientMap[clientName] = {};
    if (!clientMap[clientName][branchName])
      clientMap[clientName][branchName] = [];
    clientMap[clientName][branchName].push(cam);
  });
  return Object.entries(clientMap).map(([clientName, branches]) => ({
    clientName,
    branches: Object.entries(branches).map(([branchName, cams]) => ({
      branchName,
      cameras: cams,
    })),
  }));
}
